
import React, { useState } from "react";
import { FiPlus, FiInfo, FiTrash2 } from "react-icons/fi";
import { createCriterion } from "../services/adminSellFunnel";

const stages = [
    { value: 1, label: "۱ — مراجعه‌کننده" },
    { value: 2, label: "۲ — علاقه‌مند به محصول" },
    { value: 3, label: "۳ — علاقه‌مند به خرید" },
    { value: 4, label: "۴ — مشتری" },
    { value: 5, label: "۵ — مشتری وفادار" },
    { value: 6, label: "۶ — مشتری ناراضی" },
];

const ruleFields = [
    { value: "messageCount", label: "تعداد پیام‌ها (messageCount)" },
    {
        value: "daysSinceLastActivity",
        label: "روز از آخرین فعالیت (daysSinceLastActivity)",
    },
    { value: "chatroomCount", label: "تعداد چت‌روم‌ها (chatroomCount)" },
    {
        value: "daysSinceFirstContact",
        label: "روز از اولین تماس (daysSinceFirstContact)",
    },
];

const operators = [
    { label: "< (کوچکتر)", value: "<" },
    { label: "<= (کوچکتر مساوی)", value: "<=" },
    { label: "== (مساوی)", value: "==" },
    { label: ">= (بزرگتر مساوی)", value: ">=" },
    { label: "> (بزرگتر)", value: ">" },
];

const CreateCriterionForm = ({ accessToken, onCreated }) => {
    const [title, setTitle] = useState("");
    const [stageOrder, setStageOrder] = useState(2);
    const [description, setDescription] = useState("");
    const [type, setType] = useState("AI");
    const [prompt, setPrompt] = useState("");

    const [conditions, setConditions] = useState([
        {
            field: "messageCount",
            operator: "<",
            value: "",
        },
    ]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const handleConditionChange = (index, key, value) => {
        setConditions((prev) =>
            prev.map((condition, i) =>
                i === index
                    ? {
                        ...condition,
                        [key]: value,
                    }
                    : condition
            )
        );
    };

    const addCondition = () => {
        setConditions((prev) => [
            ...prev,
            {
                field: "messageCount",
                operator: "<",
                value: "",
            },
        ]);
    };

    const removeCondition = (index) => {
        setConditions((prev) => prev.filter((_, i) => i !== index));
    };

    const resetConditions = () => {
        setConditions([
            {
                field: "messageCount",
                operator: "<",
                value: "",
            },
        ]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError("");
        setSuccessMessage("");

        if (!title.trim()) {
            setError("عنوان معیار را وارد کنید.");
            return;
        }

        if (!description.trim()) {
            setError("توضیح معیار را وارد کنید.");
            return;
        }

        if (type === "AI" && !prompt.trim()) {
            setError("Prompt را وارد کنید.");
            return;
        }

        if (type === "SYSTEM_RULE") {
            const hasEmptyCondition = conditions.some(
                (condition) => condition.value === ""
            );

            if (hasEmptyCondition) {
                setError("مقدار همه شرط‌ها را وارد کنید.");
                return;
            }
        }

        if (!accessToken) {
            setError("توکن احراز هویت یافت نشد.");
            return;
        }

        const data = {
            title: title.trim(),
            type: type,
            stageOrder: stageOrder,
            description: description.trim(),
        };

        if (type === "AI") {
            data.prompt = prompt.trim();
        }

        if (type === "SYSTEM_RULE") {
            data.ruleConfig = {
                conditions: conditions.map((condition) => ({
                    field: condition.field,
                    operator: condition.operator,
                    value: Number(condition.value),
                })),
            };
        }

        try {
            setIsSubmitting(true);

            console.log("CREATE CRITERION DATA:", data);

            await createCriterion(data, accessToken);

            setSuccessMessage("معیار با موفقیت ایجاد شد.");

            setTitle("");
            setDescription("");
            setPrompt("");
            resetConditions();

            if (typeof onCreated === "function") {
                await onCreated();
            }
        } catch (err) {
            console.error("CREATE CRITERION ERROR:", err);

            const backendMessage =
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "ایجاد معیار با خطا مواجه شد.";

            setError(backendMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="mb-6 overflow-hidden rounded-2xl border border-teal-100 bg-white shadow-sm">
            <div className="border-b border-teal-50 bg-gradient-to-l from-teal-50/70 to-white px-5 py-4 sm:px-6">
                <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-100 text-teal-700">
                        <FiPlus size={18} />
                    </div>

                    <div>
                        <h2 className="text-lg font-bold text-[#173B3A]">
                            ایجاد معیار جدید
                        </h2>

                        <p className="mt-0.5 text-xs text-gray-500">
                            معیار موردنظر خود را برای قیف فروش تعریف کنید
                        </p>
                    </div>
                </div>
            </div>

            <div className="p-5 sm:p-6">
                <form onSubmit={handleSubmit}>
                    {/* Title + Stage */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-gray-700">
                                عنوان معیار
                                <span className="mr-1 text-xs font-normal text-gray-400">
                                    (چیزی که مدیر کسب‌وکار می‌بیند)
                                </span>
                            </label>

                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="مثلاً: درباره قیمت محصول سؤال کرده"
                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                            />
                        </div>

                        <div>
                            <label className="mb-2 block text-sm font-semibold text-gray-700">
                                دسته (stageOrder)
                            </label>

                            <select
                                value={stageOrder}
                                onChange={(e) =>
                                    setStageOrder(Number(e.target.value))
                                }
                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                            >
                                {stages.map((stage) => (
                                    <option
                                        key={stage.value}
                                        value={stage.value}
                                    >
                                        {stage.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="mt-5">
                        <label className="mb-2 block text-sm font-semibold text-gray-700">
                            توضیح داخلی
                            <span className="mr-1 text-xs font-normal text-gray-400">
                                (اختیاری، فقط برای ادمین)
                            </span>
                        </label>

                        <input
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="یادداشت داخلی درباره این معیار..."
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition placeholder:text-gray-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                        />
                    </div>

                    {/* Type Selection */}
                    <div className="mt-5">
                        <label className="mb-2 block text-sm font-semibold text-gray-700">
                            نوع معیار
                        </label>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {/* AI */}
                            <label
                                className={`
                                    flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 text-sm transition
                                    ${type === "AI"
                                        ? "border-teal-300 bg-teal-50/70 ring-1 ring-teal-100"
                                        : "border-gray-200 bg-white hover:border-teal-200"
                                    }
                                `}
                            >
                                <input
                                    type="radio"
                                    name="criteriaType"
                                    value="AI"
                                    checked={type === "AI"}
                                    onChange={(e) =>
                                        setType(e.target.value)
                                    }
                                    className="h-4 w-4 accent-teal-600"
                                />

                                <div>
                                    <div className="font-medium text-teal-800">
                                        هوش مصنوعی (AI)
                                    </div>

                                    <div className="mt-0.5 text-xs text-gray-500">
                                        دارای مصرف توکن
                                    </div>
                                </div>
                            </label>

                            {/* SYSTEM RULE */}
                            <label
                                className={`
                                    flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 text-sm transition
                                    ${type === "SYSTEM_RULE"
                                        ? "border-amber-300 bg-amber-50/70 ring-1 ring-amber-100"
                                        : "border-gray-200 bg-white hover:border-amber-200"
                                    }
                                `}
                            >
                                <input
                                    type="radio"
                                    name="criteriaType"
                                    value="SYSTEM_RULE"
                                    checked={type === "SYSTEM_RULE"}
                                    onChange={(e) =>
                                        setType(e.target.value)
                                    }
                                    className="h-4 w-4 accent-amber-500"
                                />

                                <div>
                                    <div className="font-medium text-amber-800">
                                        قانون سیستمی (SYSTEM_RULE)
                                    </div>

                                    <div className="mt-0.5 text-xs text-gray-500">
                                        بدون هزینه
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* AI Prompt */}
                    {type === "AI" && (
                        <div className="mt-5">
                            <label className="mb-2 block text-sm font-semibold text-gray-700">
                                Prompt
                                <span className="mr-1 text-xs font-normal text-gray-400">
                                    (فقط ادمین می‌بیند)
                                </span>
                            </label>

                            <textarea
                                rows={5}
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="بررسی کن آیا کاربر در مکالمه درباره قیمت محصول سؤال کرده است..."
                                className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition placeholder:text-gray-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                            />
                        </div>
                    )}

                    {/* Rule Config */}
                    {type === "SYSTEM_RULE" && (
                        <div className="mt-5">
                            {conditions.map((condition, index) => (
                                <React.Fragment key={index}>
                                    {/* AND */}
                                    {index > 0 && (
                                        <div className="my-3 flex items-center justify-center">
                                            <span className="rounded-full bg-amber-100 px-4 py-1 text-xs font-bold text-amber-700">
                                                و
                                            </span>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_1fr_auto]">
                                        {/* Field */}
                                        <div>
                                            <label className="mb-2 block text-sm font-semibold text-gray-700">
                                                فیلد
                                            </label>

                                            <select
                                                value={condition.field}
                                                onChange={(e) =>
                                                    handleConditionChange(
                                                        index,
                                                        "field",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                                            >
                                                {ruleFields.map((field) => (
                                                    <option
                                                        key={field.value}
                                                        value={field.value}
                                                    >
                                                        {field.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Operator */}
                                        <div>
                                            <label className="mb-2 block text-sm font-semibold text-gray-700">
                                                عملگر
                                            </label>

                                            <select
                                                value={condition.operator}
                                                onChange={(e) =>
                                                    handleConditionChange(
                                                        index,
                                                        "operator",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                                            >
                                                {operators.map((item) => (
                                                    <option
                                                        key={item.value}
                                                        value={item.value}
                                                    >
                                                        {item.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Value */}
                                        <div>
                                            <label className="mb-2 block text-sm font-semibold text-gray-700">
                                                مقدار
                                            </label>

                                            <input
                                                type="number"
                                                value={condition.value}
                                                onChange={(e) =>
                                                    handleConditionChange(
                                                        index,
                                                        "value",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="مثلاً 5"
                                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition placeholder:text-gray-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                                            />
                                        </div>

                                        {/* Delete */}
                                        <div className="flex items-end">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeCondition(index)
                                                }
                                                disabled={conditions.length === 1}
                                                className="mb-0 flex h-[42px] w-[42px] items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-500 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-30"
                                                title="حذف شرط"
                                            >
                                                <FiTrash2 size={17} />
                                            </button>
                                        </div>
                                    </div>
                                </React.Fragment>
                            ))}

                            {/* Add Condition */}
                            <div className="mt-4">
                                <button
                                    type="button"
                                    onClick={addCondition}
                                    className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-700 transition hover:border-amber-400 hover:bg-amber-100"
                                >
                                    <FiPlus size={17} />
                                    افزودن شرط
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {/* Success */}
                    {successMessage && (
                        <div className="mt-5 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-700">
                            {successMessage}
                        </div>
                    )}

                    {/* Submit */}
                    <div className="mt-6">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <FiPlus />

                            {isSubmitting
                                ? "در حال ایجاد..."
                                : "ایجاد معیار"}
                        </button>
                    </div>
                </form>

                {/* Notice */}
                <div className="mt-6 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-sm leading-6 text-amber-800">
                    <FiInfo className="mt-1 shrink-0 text-amber-600" />

                    <span>
                        توجه: بعد از ایجاد، تغییر Prompt یا قانون این معیار
                        روی همه‌ی کسب‌وکارهایی که از آن استفاده می‌کنند
                        بلافاصله اثر می‌گذارد و دسته‌بندی مشتریان آن‌ها دوباره
                        محاسبه می‌شود.
                    </span>
                </div>
            </div>
        </div>
    );
};

export default CreateCriterionForm;
